import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { transacoesApi, cartoesApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { formatMoney, formatDate } from '../../lib/utils'

/** Atalho direto na tela da fatura: escolhe um lançamento já existente na conta que paga o cartão
 * (o pagamento parcial ou estorno) e marca como ajuste dessa fatura — sem precisar ir em Transações. */
export function BotaoAjusteFaturaAtalho({
  cartaoId, faturaId, accountId, onAjustado,
}: { cartaoId: string; faturaId: string; accountId: string; onAjustado: () => void }) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const toast = useToast()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['transacoes-ajuste-fatura', accountId, busca],
    queryFn: () => transacoesApi.list({ accountId, excluirCartao: true, busca: busca || undefined, itensPorPagina: 20, ordenarPor: 'data', ordemDesc: true }),
    enabled: aberto,
  })
  const candidatos = data?.itens ?? []

  const ajustar = useMutation({
    mutationFn: (transacaoId: string) => cartoesApi.marcarAjusteFatura(transacaoId, cartaoId, faturaId),
    onSuccess: () => { toast.success('Ajuste lançado na fatura.'); qc.invalidateQueries(); setAberto(false); onAjustado() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <Button variant="light" icon="ri-git-commit-line" onClick={() => setAberto(true)}>+ Ajuste</Button>
      <Modal open={aberto} onClose={() => setAberto(false)} title="Ajuste de fatura">
        <p className="mb-3 text-13 text-muted">
          Escolha o lançamento que já representa o pagamento parcial ou estorno. Nasce um lançamento de ajuste
          ligado a esta fatura, reduzindo o total dela — o lançamento escolhido não é alterado.
        </p>
        <div className="mb-3">
          <Input placeholder="Buscar por descrição" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        {isLoading ? <LoadingState /> : candidatos.length === 0 ? (
          <EmptyState icon="ri-search-line" message="Nenhum lançamento encontrado nessa conta." />
        ) : (
          <div className="space-y-2">
            {candidatos.map((t) => (
              <button
                key={t.id} type="button" disabled={ajustar.isPending} onClick={() => ajustar.mutate(t.id)}
                className="flex w-full items-center justify-between gap-3 rounded border border-border/70 px-3 py-2 text-left transition-colors hover:border-primary/50 disabled:opacity-60"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-white">{t.description}</p>
                  <p className="text-2xs text-muted">{formatDate(t.date)}</p>
                </div>
                <span className="whitespace-nowrap font-semibold text-white">{formatMoney(t.amount)}</span>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}
